import axios from "axios"
import { useAuth0 } from "@auth0/auth0-react"
import { useQuery, type UseQueryResult } from "@tanstack/react-query"

interface PermissionsPayload {
	permissions: string[]
	userRole: unknown
}

export const usePermissions = (): UseQueryResult<PermissionsPayload, Error> => {
	const { getAccessTokenSilently, isAuthenticated } = useAuth0()

	return useQuery({
		queryKey: ["permissions"],
		queryFn: async () => {
			const token = await getAccessTokenSilently({
				authorizationParams: { audience: process.env.REACT_APP_API_IDENTIFIER }
			} as Parameters<typeof getAccessTokenSilently>[0])

			const headers = { Authorization: `Bearer ${token}` }
			const [permissionsRes, userRoleRes] = await Promise.all([
				axios.get<string[]>(
					`${process.env.REACT_APP_BACKEND_URL}/api/permissions`,
					{ headers }
				),
				axios.get<unknown>(`${process.env.REACT_APP_BACKEND_URL}/api/user-role`, {
					headers
				})
			])

			const payload: PermissionsPayload = {
				permissions: permissionsRes.data,
				userRole: userRoleRes.data
			}
			return payload
		},
		enabled: isAuthenticated,
		staleTime: 1000 * 60 * 5,
		refetchOnMount: "always"
	})
}

// Convenience selectors for the two common access patterns. Consumers can
// keep using the full `usePermissions()` query when they need loading/error
// state; these return just the data slice for components that only need to
// gate rendering on permissions.
export const usePermissionsList = (): string[] | null => {
	const { data } = usePermissions()
	return data?.permissions ?? null
}

export const useUserRole = (): unknown => {
	const { data } = usePermissions()
	return data?.userRole ?? []
}
